import React from 'react';
import TextLato from '../../../../text/text-lato/TextLato';
import { Header } from '../../../../headers/header/Header';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { useStore } from '../../../../../contexts/StoreContext';
import { MainLogoPressable } from 'src/components/main-logo-pressable/MainLogoPressable';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import braze from 'src/_shared/braze';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import {
  DYNAMIC_MODAL_VIEWS,
  SELECTED_MENU_ITEMS,
} from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { headerEditorTestID, pictureViewerHeaderTestID } from 'src/helpers/testIDs.helper';
import { getFormattedPrice } from 'src/helpers/price.helper';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { styles } from './PreviewHeaderDesktop.styles';
import { SaveButton } from '@photosi/rn-components/headers/components/save-button/SaveButton';
import { useAddToCartHandlers } from 'src/hooks/useAddToCartHandlers';
import { useActiveProjectPrices } from 'src/hooks/useActiveProjectPrices';
import { GhostButton } from 'src/editor-v3/components/buttons/ghost-button/GhostButton';
import { BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { PrimaryButton } from 'src/editor-v3/components/buttons/primary-button/PrimaryButton';

const LeftElement = observer(() => {
  const {
    i18n,
    project: { isProjectAddedToCart, totalNumberOfProducts, addProducts, isProductSetProject },
    dynamicModal: { openModal },
  } = useStore();

  const isNoProducts = totalNumberOfProducts === 0;
  const { isMobile } = useResponsiveScreen();

  return (
    <View style={styles.element_container}>
      <MainLogoPressable
        testID={TestIDs.PHOTOSI_LOGO}
        focusId={TestIDs.PHOTOSI_LOGO}
        focusOrder={0}
        focusGroup={FocusGroups.HEADER}
      />
      <View style={styles.group_buttons}>
        {!isNoProducts && !isProjectAddedToCart && !isProductSetProject && (
          <GhostButton
            style={styles.outline_button}
            testID={TestIDs.HEADER_ADD_PRODUCT_CTA}
            onPress={async () => {
              openModal({
                selectedMenuItem: isMobile ? SELECTED_MENU_ITEMS.Gallery : SELECTED_MENU_ITEMS.Computer,
                dynamicModalView: DYNAMIC_MODAL_VIEWS.UploadMultiSource,
                uploadMultiSourceCallback: addProducts,
              });
              amplitude.trackEvent(AnalyticsEvents.dynamicPicker.view, { type: 'manual' });
              braze.trackEvent(AnalyticsEvents.dynamicPicker.view);
              amplitude.trackEvent(AnalyticsEvents.personalize.addProductClick, {
                n_products: totalNumberOfProducts,
                position: 'header',
              });
            }}
            accessible={true}
            accessibilityLabel={i18n.t('customize:add-frame')}
            accessibilityRole="button"
            focusId={TestIDs.HEADER_ADD_PRODUCT_CTA}
            focusOrder={1}
            focusGroup={FocusGroups.HEADER}
            text={i18n.t('customize:add-frame')}
            textStyle={styles.outline_button_text}
            iconName={ICON_NAMES.AddFrame}
            iconStyle={[styles.iconAddFrame]}
            size={BUTTON_SIZES.REGULAR}
            type={BUTTON_TYPES.ICON_LEFT}
          />
        )}
        <SaveButton focusId={headerEditorTestID(TestIDs.SAVE)} focusOrder={2} focusGroup={FocusGroups.HEADER} />
      </View>
    </View>
  );
});

const RightElement = observer(() => {
  const { i18n, project } = useStore();

  const isNoProducts = project.totalNumberOfProducts === 0;

  const { estimatePrice, originalPrice } = useActiveProjectPrices();

  const { handleClickAddToCart } = useAddToCartHandlers();

  return (
    <View style={styles.element_container} testID={pictureViewerHeaderTestID()}>
      {project.totalNumberOfProducts > 0 && (
        <View style={styles.details_container}>
          {project.isProductSetProject ? (
            <TextLato style={styles.product_count} testID={TestIDs.TOTAL_PRODUCTS}>
              {i18n.t('general:set-of-frames', { count: project.totalNumberOfProducts })}
            </TextLato>
          ) : (
            <TextLato style={styles.product_count} testID={TestIDs.TOTAL_PRODUCTS}>
              {project.totalNumberOfProducts}{' '}
              {project.totalNumberOfProducts > 1 ? i18n.t('general:products') : i18n.t('general:product')}
            </TextLato>
          )}
          <View style={styles.price_container}>
            {!!originalPrice && (
              <TextLato testID={pictureViewerHeaderTestID(TestIDs.PRICE_ORIGINAL)} style={styles.formatted_original}>
                {getFormattedPrice({ price: originalPrice })}
              </TextLato>
            )}
            {!!estimatePrice && (
              <TextLato testID={pictureViewerHeaderTestID(TestIDs.PRICE_ESTIMATE)} style={styles.formatted_estimate}>
                {getFormattedPrice({ price: estimatePrice })}
              </TextLato>
            )}
          </View>
        </View>
      )}
      <View style={styles.group_buttons}>
        {!isNoProducts && (
          <PrimaryButton
            style={styles.button}
            innerStyle={styles.buttonInner}
            onPress={handleClickAddToCart}
            disabled={project.isAddToCartDisabled}
            text={project.isProjectAddedToCart ? i18n.t('customize:confirm') : i18n.t('customize:add-to-cart')}
            focusGroup={FocusGroups.HEADER}
            focusOrder={10}
            testID={headerEditorTestID(TestIDs.ADD_TO_CART)}
            focusId={headerEditorTestID(TestIDs.ADD_TO_CART)}
            accessible={true}
            accessibilityLabel={
              project.isProjectAddedToCart ? i18n.t('customize:confirm') : i18n.t('customize:add-to-cart')
            }
            accessibilityRole="button"
            type={BUTTON_TYPES.ICON_LEFT}
            size={BUTTON_SIZES.REGULAR}
            iconName={ICON_NAMES.Cart}
            iconStyle={styles.iconCart}
            textStyle={styles.button_text}
          />
        )}
      </View>
    </View>
  );
});

export const PreviewHeaderDesktop = observer(() => {
  return <Header leftElement={<LeftElement />} centerElement={<></>} rightElement={<RightElement />} />;
});
